import useRefreshSolarProposalIncentives from "~/api/hooks/useRefreshSolarProposalIncentives"

import { describeCommandError } from "../ProposalSetup/pricing/describe-command-error"
import type { IncentivesToolbarProps } from "../ProposalSetup/pricing/pricing-kind-bundle"

const REFRESHED_AT_FORMAT = new Intl.DateTimeFormat(`en-US`, {
  dateStyle: `medium`,
  timeStyle: `short`,
})

/** When the catalog was last asked for, as the seller reads it. */
export const incentivesRefreshedLabel = (refreshedAt: string | null): string =>
  refreshedAt == null
    ? `Not refreshed yet`
    : `Last refreshed ${REFRESHED_AT_FORMAT.format(new Date(refreshedAt))}`

/**
 * Refresh incentives asks the provider again for this home and the stored
 * System Pricing; the answer replaces the catalog, and the draft with it.
 */
export const SolarIncentivesRefresh = ({
  proposalId,
  refreshedAt,
  disabled,
  onRefreshed,
  onRefreshingChange,
}: IncentivesToolbarProps): JSX.Element => {
  const refresh = useRefreshSolarProposalIncentives()

  return (
    <div className="mb-4 flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p
          data-testid="solar-incentives-refreshed-at"
          className="text-[length:var(--step--2)] text-muted"
        >
          {incentivesRefreshedLabel(refreshedAt)}
        </p>
        <button
          type="button"
          disabled={disabled || refresh.isLoading}
          onClick={() => {
            onRefreshingChange(true)
            refresh.mutate(
              { proposalId },
              {
                onSuccess: onRefreshed,
                onSettled: () => onRefreshingChange(false),
              },
            )
          }}
          className="rounded-md border border-border-strong px-3 py-1.5 text-[length:var(--step--2)] font-medium text-foreground disabled:cursor-not-allowed disabled:opacity-50"
        >
          {refresh.isLoading ? `Refreshing` : `Refresh incentives`}
        </button>
      </div>
      {refresh.isError ? (
        <p role="alert" className="text-[length:var(--step--2)] text-danger">
          {describeCommandError(refresh.error, `Unable to refresh incentives`)}
        </p>
      ) : null}
    </div>
  )
}
